/**
 * Routing — who is told about which alert, in plain words. A site's notifications (escalation
 * policies), on the site's Alert settings page and on the cloud's page for that site (same
 * component; the cloud's API proxies to the site).
 *
 *   list     each notification as a sentence: "When <alert> happens on <cameras>, tell <people> by
 *            <email / WhatsApp>. If nobody acknowledges: after 5 min tell …". "Everything else"
 *            (the kind's default) last.
 *   editor   four questions — which alerts? which cameras? who should be told? what if nobody
 *            acknowledges? — with one search box for people and roles, and the same sentence on top
 *   check    "Who would be told?" for an example alert, step by step
 *
 * Every matching notification applies (a person in two is told once); "Everything else" only when
 * nothing else matched. A user only gets alerts for cameras they can see. Acknowledging (or
 * assigning / resolving / cancelling) the alert — or a system problem on the System alerts list —
 * stops the "if nobody acknowledges" steps.
 *
 * Stored as before: a policy row with levels[] and reminder — nothing here changes the API.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
  UserIcon,
  UserPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import { Callout } from '../common/Callout';
import { AASwitch, aaInputCls, aaLabelCls } from './AlertActionsControls';
import ChangeHistory from './ChangeHistory';
import {
  SYSTEM_ALERT_KINDS,
  alertActionsErrorText,
  emptyPolicyLevel,
  type PolicyContact,
  type PolicyDirectory,
  type PolicyKind,
  type PolicyLevel,
  type PolicyPreview,
  type RoutingPoliciesApi,
  type RoutingPolicy,
} from './alertActionsModel';

export interface RoutingPoliciesProps {
  api: RoutingPoliciesApi;
  kind: PolicyKind;
  canEdit: boolean;
  /** 'site' on the site's page; 'central' when the cloud shows one site. */
  perspective?: 'site' | 'central';
  /** How an AI event type reads (each app has its own labels). */
  eventTypeLabel?: (type: string) => string;
  /** Above the list — the kind switch. */
  header?: ReactNode;
}

const MAX_LEVELS = 5;
/** A small number box ("after [15] minutes") — aaInputCls is full width. */
const numCls = 'w-20 rounded-lg border border-border bg-background px-2 py-1 text-[13px] text-foreground disabled:opacity-50';
const SEVERITY_TEXT: Record<string, string> = { warning: 'warnings and critical', critical: 'critical only' };
const listText = (items: string[], max = 4) => {
  if (!items.length) return '';
  const shown = items.slice(0, max);
  const more = items.length - shown.length;
  if (more > 0) return `${shown.join(', ')} and ${more} more`;
  return shown.length > 1 ? `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}` : shown[0];
};
const viaText = (l: PolicyLevel) => [l.channels.email && 'email', l.channels.message_api && 'WhatsApp / SMS'].filter(Boolean).join(' and ') || 'no channel';

// ─── The words ──────────────────────────────────────────────────────

interface Names {
  user: Map<string, string>;
  role: Map<string, string>;
  camera: Map<string, string>;
  group: Map<string, string>;
  rule: Map<string, string>;
}

function whoOf(l: PolicyLevel | undefined, names: Names): string[] {
  if (!l) return [];
  return [
    ...l.recipients.users.map((id) => names.user.get(id) || 'a removed user'),
    ...l.recipients.roles.map((id) => `everyone with the ${names.role.get(id) || 'removed'} role`),
    ...l.recipients.contacts.filter((c) => c.name || c.email || c.phone).map((c) => c.name || c.email || c.phone),
  ];
}

/** "When … happens on …" — what a notification catches. */
function whenText(p: RoutingPolicy, kind: PolicyKind, names: Names, eventTypeLabel: (t: string) => string): ReactNode {
  if (p.is_default) return <>When <b>any other {kind === 'ai' ? 'alert' : 'system problem'}</b> happens</>;
  if (kind === 'system') {
    const kinds = p.alerts.kinds || [];
    return <>When <b>{kinds.length ? listText(kinds.map((k) => SYSTEM_ALERT_KINDS.find((x) => x.value === k)?.label.toLowerCase() || k)) : 'any system problem'}</b> happens</>;
  }
  const types = p.alerts.event_types || [];
  const rules = p.alerts.rule_ids || [];
  const parts = [
    rules.length ? `an alert from ${listText(rules.map((id) => names.rule.get(id) || 'a removed rule'), 3)}` : '',
    types.length ? `a ${listText(types.map(eventTypeLabel), 2)} alert` : '',
  ].filter(Boolean);
  const what = parts.join(' or ') || 'any alert';
  const sev = p.alerts.min_severity ? ` (${SEVERITY_TEXT[p.alerts.min_severity]})` : '';
  // Cameras are the alert rule's own; an older notification may still narrow them.
  const ids = p.cameras.ids || [];
  const map = p.cameras.mode === 'groups' ? names.group : names.camera;
  const where = p.cameras.mode === 'all' ? null : `${listText(ids.map((id) => map.get(id) || 'a removed one'), 3)}${p.cameras.mode === 'groups' ? ' cameras' : ''}`;
  return <>When <b>{what}{sev}</b> happens{where && <> on <b>{where}</b></>}</>;
}

/** "tell … by …" and the "if nobody acknowledges" steps. */
function tellText(p: RoutingPolicy, names: Names): { first: ReactNode; later: string | null } {
  const l1 = p.levels?.[0];
  const who = whoOf(l1, names);
  const first = who.length ? <>tell <b>{listText(who)}</b> by {viaText(l1)}.</> : <>tell <b className="text-destructive">nobody yet</b>.</>;
  const steps = (p.levels || []).slice(1).map((l) => `after ${l.after_min} min tell ${listText(whoOf(l, names), 3) || 'nobody'}`);
  if (p.reminder && (p.levels || []).length) steps.push(`then remind them every ${p.reminder.every_min} min (up to ${p.reminder.max}×)`);
  return { first, later: steps.length ? `If nobody acknowledges: ${steps.join(' · ')}.` : null };
}

// ─── Pickers ────────────────────────────────────────────────────────

export function Choice({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`rounded-lg border px-3 py-1.5 text-[13px] transition-colors ${on ? 'border-brand bg-brand-light font-medium text-brand' : 'border-border text-foreground hover:bg-muted'}`}
    >
      {children}
    </button>
  );
}

export function Step({ n, title, hint, children }: { n: number; title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="flex gap-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-[12px] font-semibold text-white">{n}</span>
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <h4 className="text-[14px] font-semibold text-foreground">{title}</h4>
          {hint && <p className="text-[12px] text-muted-foreground">{hint}</p>}
        </div>
        {children}
      </div>
    </section>
  );
}

/** A searchable list of checkboxes — camera groups, cameras, rules. */
export function MultiPick({ options, selected, onChange, placeholder, empty }: {
  options: { id: string; label: string; hint?: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  empty: string;
}) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter((o) => `${o.label} ${o.hint || ''}`.toLowerCase().includes(needle)) : options;
  }, [options, q]);
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="max-w-xl rounded-lg border border-border">
      <div className="flex items-center gap-1.5 border-b border-border px-2 py-1">
        <MagnifyingGlassIcon className="h-3.5 w-3.5 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="w-full bg-transparent py-0.5 text-[13px] text-foreground outline-none placeholder:text-muted-foreground" />
        {selected.length > 0 && <span className="shrink-0 text-[11px] text-muted-foreground">{selected.length} picked</span>}
      </div>
      <div className="max-h-40 overflow-y-auto py-1">
        {shown.length === 0 && <p className="px-2.5 py-1.5 text-[12px] text-muted-foreground">{empty}</p>}
        {shown.map((o) => (
          <label key={o.id} className="flex cursor-pointer items-start gap-2 px-2.5 py-1 text-[13px] text-foreground hover:bg-muted">
            <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} className="mt-0.5 accent-brand" />
            <span>{o.label}{o.hint && <span className="ml-1.5 text-[11px] text-muted-foreground">{o.hint}</span>}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * Who to tell: one search box for people and roles; picked ones become chips; people without an
 * account are added below with a name and an email or phone number.
 */
function PeoplePicker({ level, onChange, dir }: { level: PolicyLevel; onChange: (patch: Partial<PolicyLevel>) => void; dir: PolicyDirectory | null }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const r = level.recipients;
  const set = (patch: Partial<PolicyLevel['recipients']>) => onChange({ recipients: { ...r, ...patch } });
  const needle = q.trim().toLowerCase();
  const users = (dir?.users || []).filter((u) => !r.users.includes(u.id) && (!needle || `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(needle))).slice(0, 8);
  const roles = (dir?.roles || []).filter((x) => !r.roles.includes(x.id) && (!needle || x.name.toLowerCase().includes(needle))).slice(0, 5);
  const userOf = (id: string) => dir?.users.find((u) => u.id === id);
  const contacts = r.contacts;
  const setContact = (i: number, patch: Partial<PolicyContact>) => set({ contacts: contacts.map((c, j) => (j === i ? { ...c, ...patch } : c)) });

  return (
    <div className="space-y-2">
      {(r.users.length > 0 || r.roles.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {r.users.map((id) => {
            const u = userOf(id);
            const noPhone = u && !u.phone && level.channels.message_api;
            return (
              <span key={id} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted py-0.5 pl-2.5 pr-1 text-[12px] text-foreground">
                <UserIcon className="h-3.5 w-3.5 text-muted-foreground" />
                {u?.name || 'A removed user'}
                {noPhone && <span className="text-[11px] text-muted-foreground">(no phone — email only)</span>}
                <button type="button" onClick={() => set({ users: r.users.filter((x) => x !== id) })} className="rounded-full p-0.5 hover:bg-background" aria-label="Remove"><XMarkIcon className="h-3.5 w-3.5" /></button>
              </span>
            );
          })}
          {r.roles.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted py-0.5 pl-2.5 pr-1 text-[12px] text-foreground">
              <UserGroupIcon className="h-3.5 w-3.5 text-muted-foreground" />
              Everyone with the {dir?.roles.find((x) => x.id === id)?.name || 'removed'} role
              <button type="button" onClick={() => set({ roles: r.roles.filter((x) => x !== id) })} className="rounded-full p-0.5 hover:bg-background" aria-label="Remove"><XMarkIcon className="h-3.5 w-3.5" /></button>
            </span>
          ))}
        </div>
      )}

      <div ref={box} className="relative max-w-xl">
        <div className="flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1.5">
          <MagnifyingGlassIcon className="h-4 w-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Type a name or a role (e.g. supervisor)…"
            className="w-full bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        {open && (users.length > 0 || roles.length > 0) && (
          <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-card py-1 shadow-lg">
            {users.length > 0 && <p className="px-3 pb-0.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">People</p>}
            {users.map((u) => (
              <button key={u.id} type="button" onClick={() => { set({ users: [...r.users, u.id] }); setQ(''); }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-foreground hover:bg-muted">
                <UserIcon className="h-4 w-4 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{u.name}</span>
                <span className="shrink-0 text-[11px] text-muted-foreground">{[u.role, u.phone ? null : 'no phone'].filter(Boolean).join(' · ')}</span>
              </button>
            ))}
            {roles.length > 0 && <p className="px-3 pb-0.5 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Everyone with a role</p>}
            {roles.map((x) => (
              <button key={x.id} type="button" onClick={() => { set({ roles: [...r.roles, x.id] }); setQ(''); }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-foreground hover:bg-muted">
                <UserGroupIcon className="h-4 w-4 text-muted-foreground" /> Everyone with the {x.name} role
              </button>
            ))}
          </div>
        )}
      </div>

      {contacts.map((c, i) => (
        <div key={i} className="grid max-w-xl grid-cols-1 gap-1.5 sm:grid-cols-[1fr_1.3fr_1fr_auto]">
          <input value={c.name} onChange={(e) => setContact(i, { name: e.target.value })} placeholder="Name" className={`${aaInputCls} mt-0`} />
          <input value={c.email} onChange={(e) => setContact(i, { email: e.target.value })} placeholder="Email" className={`${aaInputCls} mt-0`} />
          <input value={c.phone} onChange={(e) => setContact(i, { phone: e.target.value })} placeholder="Phone, e.g. 919876543210" className={`${aaInputCls} mt-0`} />
          <button type="button" onClick={() => set({ contacts: contacts.filter((_, j) => j !== i) })} className="rounded-lg px-2 text-muted-foreground hover:bg-muted" aria-label="Remove">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => set({ contacts: [...contacts, { name: '', email: '', phone: '' }] })}
        className="inline-flex items-center gap-1 text-[12px] font-medium text-brand hover:underline"
      >
        <UserPlusIcon className="h-4 w-4" /> Add someone without an account
      </button>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <span className="text-[12px] text-muted-foreground">Send by</span>
        <Choice on={level.channels.email} onClick={() => onChange({ channels: { ...level.channels, email: !level.channels.email } })}>
          <span className="inline-flex items-center gap-1.5"><EnvelopeIcon className="h-4 w-4" /> Email</span>
        </Choice>
        <Choice on={level.channels.message_api} onClick={() => onChange({ channels: { ...level.channels, message_api: !level.channels.message_api } })}>
          <span className="inline-flex items-center gap-1.5"><ChatBubbleLeftRightIcon className="h-4 w-4" /> WhatsApp / SMS</span>
        </Choice>
      </div>
    </div>
  );
}

// ─── Editor ─────────────────────────────────────────────────────────

const nobodyIn = (l: PolicyLevel) => !l.recipients.users.length && !l.recipients.roles.length && !l.recipients.contacts.some((c) => c.email || c.phone);

function autoName(p: RoutingPolicy, kind: PolicyKind, names: Names, eventTypeLabel: (t: string) => string): string {
  if (kind === 'system') {
    const kinds = p.alerts.kinds || [];
    return kinds.length ? listText(kinds.map((k) => SYSTEM_ALERT_KINDS.find((x) => x.value === k)?.label || k), 2) : 'All system problems';
  }
  const rules = (p.alerts.rule_ids || []).map((id) => names.rule.get(id) || '?');
  const types = (p.alerts.event_types || []).map(eventTypeLabel);
  return rules.length ? listText(rules, 2) : types.length ? listText(types, 2) : 'All alerts';
}

/**
 * The escalation as a tree, drawn from the notification as it stands: what catches it → who is
 * told at once → each "if nobody acknowledges" step → reminders → never answered, with the
 * "acknowledged → stops" branch off every step.
 */
function EscalationTree({ p, kind, names, eventTypeLabel }: { p: RoutingPolicy; kind: PolicyKind; names: Names; eventTypeLabel: (t: string) => string }) {
  const levels = p.levels || [];
  const stop = (
    <span className="inline-flex shrink-0 items-center gap-1 self-center rounded-full border border-success px-2 py-0.5 text-[11px] font-medium text-success">
      ✓ acknowledged → stops here
    </span>
  );
  const Node = ({ dot, title, children, branch }: { dot: string; title: ReactNode; children?: ReactNode; branch?: boolean }) => (
    <li className="relative">
      <span className={`absolute -left-[29px] top-3 h-3.5 w-3.5 rounded-full border-2 border-card ${dot}`} />
      <div className="flex flex-wrap items-stretch gap-2">
        <div className="min-w-0 max-w-xl flex-1 rounded-lg border border-border bg-card px-3 py-2">
          <p className="text-[12px] font-semibold text-foreground">{title}</p>
          {children && <div className="text-[12px] text-muted-foreground">{children}</div>}
        </div>
        {branch && (
          <div className="flex items-center gap-1">
            <span className="h-px w-4 bg-success" />
            {stop}
          </div>
        )}
      </div>
    </li>
  );
  const who = (l: PolicyLevel) => {
    const w = whoOf(l, names);
    return w.length ? <><b className="text-foreground">{listText(w, 5)}</b> · by {viaText(l)}</> : <span className="text-destructive">nobody picked</span>;
  };
  return (
    <ol className="ml-4 space-y-3 border-l-2 border-border pl-6">
      <Node dot="bg-brand" title={kind === 'ai' ? 'An alert comes in' : 'A system problem starts'}>
        {whenText(p, kind, names, eventTypeLabel)}
      </Node>
      {levels.map((l, i) => (
        <Node
          key={i}
          dot={i === 0 ? 'bg-info' : 'bg-warning'}
          title={i === 0 ? 'Told at once' : `Still not acknowledged after ${l.after_min} min — tell`}
          branch
        >
          {who(l)}
        </Node>
      ))}
      {p.reminder && levels.length > 0 && (
        <Node dot="bg-warning" title={`Reminded every ${p.reminder.every_min} min, up to ${p.reminder.max} times`} branch>
          {who(levels[levels.length - 1])}
        </Node>
      )}
      <Node dot="bg-destructive" title="Nobody acknowledged">
        Counted as <b className="text-destructive">never answered</b> in the Escalations report.
      </Node>
    </ol>
  );
}

function PolicyEditor({ open, initial, kind, dir, names, api, eventTypeLabel, onClose, onSaved }: {
  open: boolean;
  initial: RoutingPolicy | null;
  kind: PolicyKind;
  dir: PolicyDirectory | null;
  names: Names;
  api: RoutingPoliciesApi;
  eventTypeLabel: (t: string) => string;
  onClose: () => void;
  onSaved: (p: RoutingPolicy) => void;
}) {
  const blank = (): RoutingPolicy => ({
    id: '', name: '', kind, is_default: false, enabled: true, position: 0,
    cameras: { mode: 'all', ids: [] },
    alerts: kind === 'system' ? { kinds: [] } : { event_types: [], rule_ids: [], min_severity: null },
    levels: [emptyPolicyLevel()],
    reminder: null,
  });
  const [p, setP] = useState<RoutingPolicy>(initial || blank());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // "Alerts from these rules" (picked) or "All alerts".
  const [byRule, setByRule] = useState(true);
  useEffect(() => {
    if (!open) return;
    setP(initial ? { ...initial, levels: initial.levels?.length ? initial.levels : [emptyPolicyLevel()] } : blank());
    setByRule(initial ? !!initial.alerts?.rule_ids?.length : true);
    setError('');
  }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLevel = (i: number, patch: Partial<PolicyLevel>) => setP((x) => ({ ...x, levels: x.levels.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const isDefault = p.is_default;
  const escalates = p.levels.length > 1 || !!p.reminder;
  const types = p.alerts.event_types || [];
  const toggleType = (t: string) => setP({ ...p, alerts: { ...p.alerts, event_types: types.includes(t) ? types.filter((x) => x !== t) : [...types, t] } });
  const kinds = p.alerts.kinds || [];

  const save = async () => {
    const tooEarly = p.levels.findIndex((l, i) => i > 0 && !(l.after_min > p.levels[i - 1].after_min));
    if (tooEarly > 0) {
      setError(`Step ${tooEarly + 1} has to come later than the one before it.`);
      return;
    }
    if (!isDefault && kind === 'ai' && byRule && !(p.alerts.rule_ids || []).length && !(p.alerts.event_types || []).length) {
      setError('Pick at least one alert rule — or choose "All alerts".');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body: Partial<RoutingPolicy> = {
        enabled: p.enabled,
        reminder: p.reminder || null,
        levels: p.levels.map((l) => ({ ...l, recipients: { ...l.recipients, contacts: l.recipients.contacts.filter((c) => c.name || c.email || c.phone) } })),
      };
      if (!isDefault) {
        Object.assign(body, {
          kind: p.kind,
          name: p.name.trim() || autoName(p, kind, names, eventTypeLabel),
          cameras: p.cameras,
          alerts: kind === 'system' ? p.alerts : byRule ? p.alerts : { ...p.alerts, rule_ids: [], event_types: [] },
        });
      }
      onSaved(await api.save(initial?.id || null, body));
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  const summary = tellText(p, names);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={isDefault ? 'Everything else' : initial ? 'Edit notification' : 'New notification'}
      subtitle={isDefault ? `${kind === 'ai' ? 'Alerts' : 'System problems'} that no other notification catches.` : 'Answer the questions — the sentence at the top says what will happen.'}
      footer={(
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1 text-[12px] text-destructive">{error}</span>
          <button type="button" onClick={onClose} className="rounded-lg border border-border px-3 py-1.5 text-[13px] text-foreground hover:bg-muted">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="rounded-lg bg-brand px-4 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    >
      <div className="space-y-5 p-5">
        <p className="rounded-xl border border-border bg-muted px-3 py-2.5 text-[13px] leading-relaxed text-foreground">
          {whenText(p, kind, names, eventTypeLabel)}, {summary.first}
          {summary.later && <span className="mt-1 block">{summary.later}</span>}
        </p>

        {!isDefault && (
          <Step n={1} title="Name">
            <input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} placeholder={autoName(p, kind, names, eventTypeLabel)} className={`${aaInputCls} max-w-md`} />
          </Step>
        )}

        {!isDefault && (
          <Step n={2} title={kind === 'ai' ? 'Which alerts?' : 'Which problems?'} hint={kind === 'ai' ? 'Pick the alert rules whose alerts this notification is for. Cameras are set on each rule.' : 'Nothing picked = all of them.'}>
            {kind === 'ai' ? (
              <>
                <div className="flex flex-wrap gap-1.5">
                  <Choice on={byRule} onClick={() => setByRule(true)}>Alerts from these rules</Choice>
                  <Choice on={!byRule} onClick={() => setByRule(false)}>All alerts</Choice>
                </div>
                {byRule && (
                  <MultiPick
                    options={(dir?.rules || []).map((r) => ({ id: r.id, label: r.name || eventTypeLabel(r.event_type), hint: [eventTypeLabel(r.event_type), r.is_active ? null : '(off)'].filter(Boolean).join(' · ') }))}
                    selected={p.alerts.rule_ids || []}
                    onChange={(rule_ids) => setP({ ...p, alerts: { ...p.alerts, rule_ids } })}
                    placeholder="Find an alert rule"
                    empty="This site has no alert rules yet — create one on the Alert Config tab."
                  />
                )}
                {byRule && types.length > 0 && (
                  <div className="flex max-w-xl flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
                    Also catches these alert types (an older setting):
                    {types.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted py-0.5 pl-2 pr-1 text-foreground">
                        {eventTypeLabel(t)}
                        <button type="button" onClick={() => toggleType(t)} className="rounded-full p-0.5 hover:bg-background" aria-label="Remove"><XMarkIcon className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[12px] text-muted-foreground">How serious?</span>
                  <Choice on={!p.alerts.min_severity} onClick={() => setP({ ...p, alerts: { ...p.alerts, min_severity: null } })}>All</Choice>
                  <Choice on={p.alerts.min_severity === 'warning'} onClick={() => setP({ ...p, alerts: { ...p.alerts, min_severity: 'warning' } })}>Warnings and critical</Choice>
                  <Choice on={p.alerts.min_severity === 'critical'} onClick={() => setP({ ...p, alerts: { ...p.alerts, min_severity: 'critical' } })}>Critical only</Choice>
                </div>
                {p.cameras.mode !== 'all' && (
                  <p className="flex max-w-xl flex-wrap items-center gap-2 rounded-lg border border-warning px-3 py-2 text-[12px] text-foreground">
                    This notification is also limited to {p.cameras.mode === 'groups' ? 'camera groups' : 'cameras'}:{' '}
                    <b>{listText((p.cameras.ids || []).map((id) => (p.cameras.mode === 'groups' ? names.group : names.camera).get(id) || '?'), 4)}</b> (an older setting).
                    <button type="button" onClick={() => setP({ ...p, cameras: { mode: 'all', ids: [] } })} className="font-medium text-brand hover:underline">Remove this limit</button>
                  </p>
                )}
              </>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                <Choice on={kinds.length === 0} onClick={() => setP({ ...p, alerts: { kinds: [] } })}>Any problem</Choice>
                {SYSTEM_ALERT_KINDS.map((k) => (
                  <Choice key={k.value} on={kinds.includes(k.value)} onClick={() => setP({ ...p, alerts: { kinds: kinds.includes(k.value) ? kinds.filter((x) => x !== k.value) : [...kinds, k.value] } })}>
                    {k.label}
                  </Choice>
                ))}
              </div>
            )}
          </Step>
        )}

        <Step
          n={isDefault ? 1 : 3}
          title="Who should be told?"
          hint="People only get alerts from cameras they are allowed to see."
        >
          <PeoplePicker level={p.levels[0]} onChange={(patch) => setLevel(0, patch)} dir={dir} />
          {nobodyIn(p.levels[0]) && !escalates && <Callout tone="warning">Nobody is picked yet — nobody would be told.</Callout>}
        </Step>

        <Step
          n={isDefault ? 2 : 4}
          title="If nobody acknowledges…"
          hint={kind === 'ai'
            ? 'Tell more people when the alert is still not acknowledged. Acknowledging it in TRINETRA stops this.'
            : 'Tell more people when the problem is still not acknowledged. Acknowledging it in the System alerts list stops this — CPU, memory and disk also stop when they recover.'}
        >
          <label className="flex items-center gap-3">
            <AASwitch
              checked={escalates}
              onChange={(on) => setP((x) => (on
                ? { ...x, levels: x.levels.length > 1 ? x.levels : [...x.levels, emptyPolicyLevel(5)] }
                : { ...x, levels: x.levels.slice(0, 1), reminder: null }))}
              label="Tell more people if nobody acknowledges"
            />
            <span className="text-[13px] text-foreground">{escalates ? 'Yes, tell more people' : 'No — only the people above'}</span>
          </label>
          {escalates && (
            <div className="space-y-3">
              {p.levels.slice(1).map((l, k) => {
                const i = k + 1;
                return (
                  <div key={i} className="space-y-2 rounded-xl border border-warning p-3">
                    <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium text-foreground">
                      If still nobody after
                      <input
                        type="number" min={1} max={1440} value={l.after_min}
                        onChange={(e) => setLevel(i, { after_min: Number(e.target.value) })}
                        className={numCls} aria-label={`Minutes for step ${i + 1}`}
                      />
                      minutes, also tell:
                      <span className="flex-1" />
                      <button type="button" onClick={() => setP((x) => ({ ...x, levels: x.levels.filter((_, j) => j !== i) }))}
                        className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-destructive" aria-label={`Remove step ${i + 1}`}>
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <PeoplePicker level={l} onChange={(patch) => setLevel(i, patch)} dir={dir} />
                  </div>
                );
              })}
              {p.levels.length < MAX_LEVELS && (
                <button
                  type="button"
                  onClick={() => setP((x) => ({ ...x, levels: [...x.levels, emptyPolicyLevel(x.levels[x.levels.length - 1].after_min + 10)] }))}
                  className="inline-flex items-center gap-1 text-[12px] font-medium text-brand hover:underline"
                >
                  <PlusIcon className="h-4 w-4" /> Then, if still nobody, tell someone else
                </button>
              )}
              <div className="flex flex-wrap items-center gap-2 text-[13px] text-foreground">
                <label className="flex items-center gap-1.5">
                  <input type="checkbox" checked={!!p.reminder} onChange={(e) => setP({ ...p, reminder: e.target.checked ? { every_min: 5, max: 3 } : null })} className="accent-brand" />
                  Keep reminding the last people every
                </label>
                <input type="number" min={1} max={1440} disabled={!p.reminder} value={p.reminder?.every_min ?? 5}
                  onChange={(e) => setP({ ...p, reminder: { every_min: Number(e.target.value), max: p.reminder?.max ?? 3 } })}
                  className={numCls} aria-label="Remind every (minutes)" />
                <span>minutes, up to</span>
                <input type="number" min={1} max={10} disabled={!p.reminder} value={p.reminder?.max ?? 3}
                  onChange={(e) => setP({ ...p, reminder: { every_min: p.reminder?.every_min ?? 5, max: Number(e.target.value) } })}
                  className={numCls} aria-label="How many reminders" />
                <span>times</span>
              </div>
            </div>
          )}
        </Step>

        <section className="space-y-2 rounded-xl border border-border p-4">
          <h4 className="text-[14px] font-semibold text-foreground">How it will work</h4>
          <p className="text-[12px] text-muted-foreground">Drawn from your answers above — it changes as you edit.</p>
          <EscalationTree p={p} kind={kind} names={names} eventTypeLabel={eventTypeLabel} />
        </section>
      </div>
    </Modal>
  );
}

// ─── "Who would be told?" ───────────────────────────────────────────

function WhoGetsThis({ api, kind, dir, eventTypeLabel }: { api: RoutingPoliciesApi; kind: PolicyKind; dir: PolicyDirectory | null; eventTypeLabel: (t: string) => string }) {
  const [sample, setSample] = useState<Record<string, string>>({});
  const [result, setResult] = useState<PolicyPreview | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setResult(null); setSample({}); }, [kind]);
  const eventTypes = useMemo(() => [...new Set((dir?.rules || []).map((r) => r.event_type).filter((t) => t && t !== '*'))].sort(), [dir]);

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      setResult(await api.preview(kind, sample));
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not work it out'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-border bg-card px-4 py-3">
      <h3 className="text-[14px] font-semibold text-foreground">Check: who would be told?</h3>
      <p className="text-[12px] text-muted-foreground">Pick an example and see exactly who gets it.</p>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        {kind === 'ai' ? (
          <>
            <label className="min-w-[12rem] flex-1">
              <span className={aaLabelCls}>If this camera</span>
              <select value={sample.camera_id || ''} onChange={(e) => setSample({ ...sample, camera_id: e.target.value })} className={aaInputCls}>
                <option value="">Pick a camera</option>
                {(dir?.cameras || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="min-w-[10rem] flex-1">
              <span className={aaLabelCls}>sees</span>
              <select value={sample.event_type || ''} onChange={(e) => setSample({ ...sample, event_type: e.target.value })} className={aaInputCls}>
                <option value="">any alert</option>
                {eventTypes.map((t) => <option key={t} value={t}>{eventTypeLabel(t)}</option>)}
              </select>
            </label>
            <label className="w-36">
              <span className={aaLabelCls}>and it is</span>
              <select value={sample.severity || 'critical'} onChange={(e) => setSample({ ...sample, severity: e.target.value })} className={aaInputCls}>
                <option value="critical">critical</option>
                <option value="warning">a warning</option>
                <option value="normal">for information</option>
              </select>
            </label>
          </>
        ) : (
          <label className="min-w-[12rem] flex-1">
            <span className={aaLabelCls}>If this happens</span>
            <select value={sample.kind || 'service'} onChange={(e) => setSample({ kind: e.target.value })} className={aaInputCls}>
              {SYSTEM_ALERT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </label>
        )}
        <button type="button" onClick={run} disabled={busy || (kind === 'ai' && !sample.camera_id)} className="rounded-lg bg-brand px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50">
          {busy ? 'Checking…' : 'Check'}
        </button>
      </div>
      {error && <p className="mt-2 text-[12px] text-destructive">{error}</p>}
      {result && (
        <div className="mt-3 space-y-2 text-[13px]">
          <p className="text-muted-foreground">
            Caught by: {result.policies.filter((x) => !(result.fallback && x.is_default)).map((x) => x.is_default ? 'Everything else' : x.name).join(', ') || 'nothing'}
          </p>
          {result.fallback && (
            <Callout tone="warning">
              Nobody on {result.fallback.policies.map((n) => `"${n}"`).join(', ')} can be reached right now ({result.fallback.reason}),
              so <b>Everything else</b> is told straight away instead — the alert is never lost.
            </Callout>
          )}
          {result.levels.map((l) => (
            <div key={l.level} className="rounded-lg border border-border px-3 py-2">
              <p className="mb-1 text-[12px] font-semibold text-foreground">
                {l.level === 1 ? 'Told straight away' : `If nobody acknowledges — step ${l.level}`}
              </p>
              {l.people.length === 0
                ? <p className="text-muted-foreground">Nobody.</p>
                : <p className="text-foreground">{l.people.map((x) => `${x.name} (${[x.email && 'email', x.phone && 'WhatsApp'].filter(Boolean).join(' + ')})`).join(', ')}</p>}
              {l.left_out.length > 0 && <p className="mt-1 text-[12px] text-muted-foreground">Not told: {l.left_out.map((x) => `${x.name} — ${x.reason}`).join('; ')}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── The list ───────────────────────────────────────────────────────

export default function RoutingPolicies({ api, kind, canEdit, perspective = 'site', eventTypeLabel = (t) => t, header }: RoutingPoliciesProps) {
  const [policies, setPolicies] = useState<RoutingPolicy[] | null>(null);
  const [dir, setDir] = useState<PolicyDirectory | null>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<RoutingPolicy | null | 'new'>(null);
  const [deleting, setDeleting] = useState<RoutingPolicy | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [treeFor, setTreeFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError('');
    const [list, directory] = await Promise.allSettled([api.list(kind), api.directory()]);
    if (list.status === 'fulfilled') setPolicies(list.value);
    else setError(alertActionsErrorText(list.reason, 'Could not read who is notified'));
    if (directory.status === 'fulfilled') setDir(directory.value);
  }, [api, kind]);
  useEffect(() => { setPolicies(null); load(); }, [load]);

  const names: Names = useMemo(() => ({
    user: new Map((dir?.users || []).map((u) => [u.id, u.name])),
    role: new Map((dir?.roles || []).map((r) => [r.id, r.name])),
    camera: new Map((dir?.cameras || []).map((c) => [c.id, c.name])),
    group: new Map((dir?.groups || []).map((g) => [g.id, g.name])),
    rule: new Map((dir?.rules || []).map((r) => [r.id, r.name || eventTypeLabel(r.event_type)])),
  }), [dir, eventTypeLabel]);

  const toggle = async (p: RoutingPolicy, enabled: boolean) => {
    setBusyId(p.id);
    try {
      const saved = await api.save(p.id, { enabled });
      setPolicies((list) => (list || []).map((x) => (x.id === p.id ? saved : x)));
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not change it'));
    } finally {
      setBusyId(null);
    }
  };

  const ordered = useMemo(() => [...(policies || [])].sort((a, b) => Number(a.is_default) - Number(b.is_default) || a.position - b.position), [policies]);
  const where = perspective === 'site' ? 'this site' : 'the site';

  return (
    <div className="space-y-3">
      {header}
      <ul className="space-y-1 rounded-xl border border-border bg-card px-4 py-3 text-[13px] text-foreground">
        <li>• Each card below says <b>which alerts</b> go to <b>which people</b>, and by email or WhatsApp.</li>
        <li>• If an alert fits more than one card, everyone on them is told — each person once.</li>
        <li>• People only get alerts from cameras they are allowed to see on {where}.</li>
        <li>• If nobody acknowledges in time, a card can tell more people — acknowledging stops it.</li>
        <li>• If nobody on a card can be reached (no access to the camera, no phone), <b>Everything else</b> is told instead.</li>
      </ul>
      {error && <Callout tone="danger">{error}</Callout>}

      <div className="flex flex-wrap items-center gap-2">
        <h3 className="min-w-0 flex-1 text-[15px] font-semibold text-foreground">
          {kind === 'ai' ? 'Who is told about AI alerts' : 'Who is told about system problems'}
        </h3>
        <button type="button" onClick={load} className="rounded-lg border border-border p-1.5 text-muted-foreground hover:bg-muted" title="Reload">
          <ArrowPathIcon className="h-4 w-4" />
        </button>
        {canEdit && (
          <button type="button" onClick={() => setEditing('new')} className="inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90">
            <PlusIcon className="h-4 w-4" /> Add who to notify
          </button>
        )}
      </div>

      {policies === null && <p className="text-[13px] text-muted-foreground">Loading…</p>}
      <div className="space-y-2">
        {ordered.map((p) => {
          const tell = tellText(p, names);
          return (
            <article key={p.id} className={`flex gap-3 rounded-xl border bg-card px-4 py-3 ${p.is_default ? 'border-dashed border-border' : 'border-border'} ${p.enabled ? '' : 'opacity-60'}`}>
              <div className="pt-0.5">
                <AASwitch checked={p.enabled} onChange={(v) => toggle(p, v)} disabled={!canEdit} busy={busyId === p.id} label={p.enabled ? 'Turn off' : 'Turn on'} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold text-foreground">
                  {p.is_default ? 'Everything else' : p.name}
                  {!p.enabled && <span className="ml-2 text-[12px] font-normal text-muted-foreground">(off)</span>}
                </p>
                <p className="text-[13px] leading-relaxed text-foreground">{whenText(p, kind, names, eventTypeLabel)}, {tell.first}</p>
                {tell.later && <p className="text-[12px] text-muted-foreground">{tell.later}</p>}
                <button type="button" onClick={() => setTreeFor(treeFor === p.id ? null : p.id)} className="mt-1 text-[12px] font-medium text-brand hover:underline">
                  {treeFor === p.id ? 'Hide' : 'Show how it works'}
                </button>
                {treeFor === p.id && <div className="mt-2"><EscalationTree p={p} kind={kind} names={names} eventTypeLabel={eventTypeLabel} /></div>}
              </div>
              {canEdit && (
                <div className="flex shrink-0 items-start gap-1">
                  <button type="button" onClick={() => setEditing(p)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-[12px] text-foreground hover:bg-muted">
                    <PencilSquareIcon className="h-4 w-4" /> Edit
                  </button>
                  {!p.is_default && (
                    <button type="button" onClick={() => setDeleting(p)} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive" title="Delete">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>

      <WhoGetsThis api={api} kind={kind} dir={dir} eventTypeLabel={eventTypeLabel} />
      {api.history && <ChangeHistory load={api.history} entity="policy" title="Change history" />}

      <PolicyEditor
        open={editing !== null}
        initial={editing === 'new' ? null : editing}
        kind={kind}
        dir={dir}
        names={names}
        api={api}
        eventTypeLabel={eventTypeLabel}
        onClose={() => setEditing(null)}
        onSaved={(saved) => {
          setPolicies((list) => [...(list || []).filter((x) => x.id !== saved.id), saved]);
          setEditing(null);
        }}
      />
      <ConfirmDialog
        open={!!deleting}
        title="Delete this notification?"
        message={deleting ? `"${deleting.name}" will stop telling its people. Its alerts go to the other cards, or to "Everything else".` : ''}
        confirmLabel="Delete"
        variant="danger"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await api.remove(deleting.id);
            setPolicies((list) => (list || []).filter((x) => x.id !== deleting.id));
          } catch (err) {
            setError(alertActionsErrorText(err, 'Could not delete it'));
          }
          setDeleting(null);
        }}
      />
    </div>
  );
}
